// eslint-disable-next-line wix-custom-rules/wixstores-file-names
import {Scope} from '@wix/app-settings-client';
import {I$WWrapper} from '@wix/native-components-infra/dist/es/src/types/types';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {PubSubEvents} from '@wix/wixstores-client-core';
import {CreateControllerFn} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {GALLERY_TYPE} from '../constants';
import type {VeloInputs} from '../types/galleryTypes';
import {IGalleryControllerConfig} from '../types/galleryTypes';
import {SliderGalleryStore} from './SliderGalleryStore';
import {GalleryWidgetViewState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/enums/galleryWidgetViewState';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;

export const SliderGalleryController: CreateControllerFn = (controllerParams) => {
  const siteStore = controllerParams.appData.context.siteStore;
  const {controllerConfig, flowAPI} = controllerParams;
  const {setProps, compId, type} = controllerConfig;

  let sliderGalleryStore: SliderGalleryStore;
  let veloInputs: VeloInputs;
  let onReadyResolver: ResolveFn<void>;
  let initializeCallback: Function;
  let config: IGalleryControllerConfig = _.clone(controllerConfig.config);

  const waitUntilReady = new Promise<void>((resolve) => {
    onReadyResolver = resolve;
  });

  const slotAPIFactory = createSlotVeloAPIFactory(controllerConfig);

  const initializeVeloDataAfterStoreCreated = async () => {
    await waitUntilReady;
    sliderGalleryStore.setVeloInputs(veloInputs);
    return sliderGalleryStore.setInitialState();
  };

  const setVeloInputs = (newVeloInputs: VeloInputs) => {
    veloInputs = {
      ...veloInputs,
      ...newVeloInputs,
    };
  };

  const exports = () => {
    return {
      setCollection: async (collectionId: string): Promise<void> => {
        setVeloInputs({collectionId, productIds: undefined});
        if (sliderGalleryStore) {
          return initializeVeloDataAfterStoreCreated();
        }
      },
      setProducts: async (productIds: string[]) => {
        setVeloInputs({productIds, collectionId: undefined});
        if (sliderGalleryStore) {
          return initializeVeloDataAfterStoreCreated();
        }
      },
      onInitialize: (cb: Function) => {
        initializeCallback = cb;
      },
    };
  };

  const updateConfig = (_$w: I$WWrapper, newConfig: IGalleryControllerConfig) => {
    config = _.clone(newConfig);
    void sliderGalleryStore.updateState(config);
  };

  const updateAppSettings = (_$w: I$WWrapper, updates: {[key: string]: any}) => {
    /* istanbul ignore else */
    if (updates.scope === Scope.COMPONENT && updates.source === 'app-settings') {
      void sliderGalleryStore.updateState(config, {APP: undefined, COMPONENT: undefined, appSettings: updates.payload});
    }
  };

  const subscribeToEvents = () => {
    if (
      sliderGalleryStore.galleryType === GALLERY_TYPE.RELATED_PRODUCTS ||
      sliderGalleryStore.galleryType === GALLERY_TYPE.ALGORITHMS
    ) {
      siteStore.pubSub.subscribe(
        PubSubEvents.RELATED_PRODUCTS,
        (response) => {
          sliderGalleryStore.renderRelatedProducts(response.data);
        },
        true
      );
    }
  };

  const load = async () => {
    if (initializeCallback) {
      await Promise.resolve(initializeCallback()).catch(flowAPI.reportError);
    }
    sliderGalleryStore = new SliderGalleryStore(
      config,
      controllerParams.dangerousPublicDataOverride,
      setProps,
      siteStore,
      compId,
      type,
      flowAPI,
      flowAPI.reportError,
      slotAPIFactory,
      veloInputs
    );

    subscribeToEvents();

    await sliderGalleryStore
      .setInitialState()
      .then(() => onReadyResolver())
      .catch(flowAPI.reportError);
  };

  const init = async () => {
    await siteStore.loadClientConfig();
    controllerParams.controllerConfig.wixCodeApi.location.onChange(() => void load());
    await load();
  };

  /* istanbul ignore next */
  const updateWidgetViewState = (viewState: GalleryWidgetViewState) => {
    void sliderGalleryStore.updateWidgetViewState(viewState);
  };

  return {
    async pageReady() {
      await init();
    },
    load,
    exports,
    updateConfig,
    updateAppSettings,
    updateWidgetViewState,
  };
};
